import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ejot-platform-theme';

/**
 * Light is the default, matching RebarFix. A chosen theme is remembered for
 * the browser session only — localStorage would outlive a shared workstation's
 * user, which is exactly the kind of surprise a shop-floor PC does not need.
 */
function readStoredTheme(): Theme {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    // Private mode or blocked storage: fall back to the default.
    return 'light';
  }
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggleTheme };
}
